import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
`;

/* ─── PAGE WRAPPER ─── */
export const PageWrapper = styled.div`
  min-height: calc(100vh - 80px);
  background: var(--mp-bodyBackground, #f7f8fc);
  animation: ${fadeUp} 0.35s ease-out;
`;

export const Container = styled.div`
  max-width: 1680px;
  margin: 0 auto;
  padding: 40px 40px 80px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 24px 16px 60px;
  }
`;

/* ─── PAGE HEADER ─── */
export const PageHeader = styled.div`
  margin-bottom: 32px;
`;

export const PageTitle = styled.h1`
  font-family: 'Liberation Serif', serif;
  font-size: 36px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);
  margin: 0 0 6px;

  span {
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

export const PageSubtitle = styled.p`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: var(--mp-featureDescColor, #71717a);
  margin: 0;
`;

/* ─── FILTER TABS ─── */
export const FilterTabsRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 28px;
`;

export const FilterTab = styled.button<{ $active: boolean }>`
  padding: 8px 18px;
  border-radius: 100px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  border: ${p => p.$active ? "none" : "1.5px solid rgba(201, 77, 170, 0.3)"};
  background: ${p => p.$active ? "linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%)" : "transparent"};
  color: ${p => p.$active ? "#ffffff" : "#c94daa"};
  box-shadow: ${p => p.$active ? "0 2px 10px rgba(201,77,170,0.35)" : "none"};

  &:hover {
    background: ${p => p.$active ? "linear-gradient(95.57deg, #FFB020 0%, #b03d97 100%)" : "rgba(201,77,170,0.06)"};
    transform: translateY(-1px);
  }

  [data-marketplace-theme="dark"] & {
    border-color: ${p => p.$active ? "none" : "rgba(255,201,81,0.3)"};
    color: ${p => p.$active ? "#ffffff" : "#FFC951"};
  }
`;

export const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.3);
  border-radius: 100px;
  padding: 1px 7px;
  font-size: 10px;
  font-weight: 800;
  margin-left: 6px;
`;

/* ─── RESULTS ROW ─── */
export const ResultsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

export const ResultCount = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--mp-featureDescColor, #71717a);
`;

/* ─── CARD GRID ─── */
export const SavedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

/* ─── INDIVIDUAL CARD ─── */
export const SavedCard = styled.div`
  border-radius: 16px;
  overflow: hidden;
  background: var(--mp-cardBackground, #ffffff);
  border: 1px solid rgba(23,23,23,0.06);
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  position: relative;

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #15181b;
    border-color: rgba(255,255,255,0.06);
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 40px rgba(0,0,0,0.1);
  }
`;

export const CardImageWrap = styled.div`
  width: 100%;
  aspect-ratio: 1.5;
  position: relative;
  overflow: hidden;
  background: var(--mp-bodyBackground, #f0f0f0);

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #1e2226;
  }
`;

export const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const CardImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  background: linear-gradient(135deg, rgba(255,201,81,0.08) 0%, rgba(201,77,170,0.08) 100%);
`;

export const TypeBadge = styled.span<{ $type: string }>`
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 3px 10px;
  border-radius: 20px;
  font-family: Montserrat, sans-serif;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  background: ${p => {
    if (p.$type === "rent") return "#0ea5e9";
    if (p.$type === "sale") return "#7c4dff";
    if (p.$type === "photographer") return "#c94daa";
    if (p.$type === "videographer") return "#f09a3e";
    if (p.$type === "equipment_requirement") return "#059669";
    if (p.$type === "service_requirement") return "#db2777";
    return "#6b7280";
  }};
  color: #ffffff;
`;

export const UnavailableBadge = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);

  span {
    background: rgba(239, 68, 68, 0.9);
    color: #fff;
    font-family: Montserrat, sans-serif;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    padding: 6px 14px;
    border-radius: 100px;
  }
`;

export const UnsaveBtn = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff6b6b 0%, #c94daa 100%);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #ffffff;
  box-shadow: 0 2px 10px rgba(201,77,170,0.4);
  transition: transform 0.15s ease, opacity 0.15s ease;
  animation: ${pulse} 0.3s ease;

  svg { fill: #ffffff; }

  &:hover {
    transform: scale(1.12);
    opacity: 0.88;
  }

  &:active { transform: scale(0.9); }
`;

/* ─── CARD BODY ─── */
export const CardBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 6px;
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-family: 'Liberation Serif', serif;
  font-size: 16px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const CardDesc = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: var(--mp-featureDescColor, #71717a);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

export const CardMetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  color: var(--mp-featureDescColor, #71717a);
  font-weight: 500;

  svg { opacity: 0.6; }
`;

export const CardPrice = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: var(--mp-headingColor, #171717);
  margin-top: auto;
  padding-top: 8px;

  span {
    font-size: 10px;
    font-weight: 500;
    color: var(--mp-featureDescColor, #71717a);
    margin-left: 3px;
  }
`;

export const CardFooter = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 16px 16px;
`;

export const ViewBtn = styled.button`
  flex: 1;
  padding: 9px;
  border-radius: 8px;
  border: none;
  background: var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%));
  color: #ffffff;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover { opacity: 0.88; }
`;

export const RemoveBtn = styled.button`
  padding: 9px 12px;
  border-radius: 8px;
  border: 1.5px solid rgba(239, 68, 68, 0.4);
  background: transparent;
  color: #ef4444;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: rgba(239, 68, 68, 0.06);
    border-color: #ef4444;
  }
`;

/* ─── REQUIREMENT CARD ─── */
export const ReqCard = styled.div`
  border-radius: 16px;
  overflow: hidden;
  background: var(--mp-cardBackground, #ffffff);
  border: 1px solid rgba(23,23,23,0.06);
  display: flex;
  flex-direction: column;
  cursor: default;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  position: relative;

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #15181b;
    border-color: rgba(255,255,255,0.06);
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 32px rgba(0,0,0,0.08);
  }
`;

export const ReqCardHeader = styled.div`
  padding: 16px 16px 10px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

export const ReqCardBody = styled.div`
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

/* ─── SKELETON ─── */
export const SkeletonCard = styled.div`
  border-radius: 16px;
  overflow: hidden;
  background: var(--mp-cardBackground, #ffffff);
  border: 1px solid rgba(23,23,23,0.06);

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #15181b;
    border-color: rgba(255,255,255,0.06);
  }
`;

export const SkeletonBlock = styled.div<{ $h?: number; $w?: string; $mb?: number }>`
  height: ${p => p.$h ?? 16}px;
  width: ${p => p.$w ?? "100%"};
  border-radius: 6px;
  background: linear-gradient(90deg, rgba(23,23,23,0.06) 25%, rgba(23,23,23,0.03) 50%, rgba(23,23,23,0.06) 75%);
  background-size: 600px 100%;
  animation: shimmer 1.4s infinite linear;
  margin-bottom: ${p => p.$mb ?? 0}px;

  @keyframes shimmer {
    0%   { background-position: -600px 0; }
    100% { background-position: 600px 0; }
  }

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0.05) 75%);
    background-size: 600px 100%;
    animation: shimmer 1.4s infinite linear;
  }
`;

/* ─── EMPTY STATE ─── */
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 80px 20px;
  text-align: center;
`;

export const EmptyEmoji = styled.div`
  font-size: 56px;
  line-height: 1;
`;

export const EmptyTitle = styled.h2`
  margin: 0;
  font-family: 'Liberation Serif', serif;
  font-size: 24px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);
`;

export const EmptyDesc = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: var(--mp-featureDescColor, #71717a);
  max-width: 320px;
  line-height: 1.6;
`;

export const EmptyBtn = styled.button`
  padding: 12px 28px;
  border-radius: 100px;
  border: none;
  background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
  color: #ffffff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s ease;
  box-shadow: 0 4px 18px rgba(201,77,170,0.35);
  margin-top: 8px;

  &:hover { opacity: 0.88; }
`;

/* ─── LOAD MORE ─── */
export const LoadMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
`;

export const LoadMoreBtn = styled.button`
  padding: 12px 32px;
  border-radius: 100px;
  border: 1.5px solid rgba(201,77,170,0.4);
  background: transparent;
  color: #c94daa;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(201,77,170,0.06);
    border-color: #c94daa;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  [data-marketplace-theme="dark"] & {
    border-color: rgba(255,201,81,0.4);
    color: #FFC951;
    &:hover { background: rgba(255,201,81,0.06); border-color: #FFC951; }
  }
`;
